import { Injectable, Injector } from "@angular/core";
import { Subject } from "rxjs";
import * as THREE from "three";
import { Object3DUserData } from "../utils/shape";
import { ComponentInteractionService } from "./services/component-interaction/component-interaction.service";
import { InteractionService } from "./shared/helpers/interaction.service";
import { UndoRedoService } from "./services/undoRedo/undo-redo.service";
import { Action, UndoRedoAction } from "~/src/app/shared/models/undoRedo.model";
import { ShapeValidationService } from "./services/shape-validation.service";
@Injectable({
    providedIn: "root",
})
export class ActionHelperService {
    private isRotatedSubject = new Subject<boolean>();
    public isRotated$ = this.isRotatedSubject.asObservable();
    private selectedShapes: THREE.Mesh[] = [];

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
        private undoRedoService: UndoRedoService,
        private injector: Injector,
    ) {
        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            this.selectedShapes = selections.map((selection) => selection.mesh);
        });
    }

    private get shapeValidationService(): ShapeValidationService {
        return this.injector.get(ShapeValidationService);
    }
    // Call this method wherever you modify `isEditted`
    private markAsEdited() {
        this.isRotatedSubject.next(true);
    }

    private calculateProposedQuaternion(axis: string, angleInRad: number): THREE.Quaternion {
        const rotationQuaternion = new THREE.Quaternion();
        const rotationAxis = new THREE.Vector3();
        switch (axis) {
            case "x":
                rotationAxis.set(1, 0, 0);
                break;
            case "y":
                rotationAxis.set(0, 1, 0);
                break;
            case "z":
                rotationAxis.set(0, 0, 1);
                break;
        }
        rotationQuaternion.setFromAxisAngle(rotationAxis, angleInRad);
        return rotationQuaternion;
    }

    private performRotationAsync(
        axis: string,
        mesh: THREE.Mesh,
        inverse: boolean = false,
        angleInDegrees: number = 90,
    ): Promise<void> {
        return new Promise((resolve) => {
            // Convert angle from degrees to radians
            let angleInRad = THREE.MathUtils.degToRad(angleInDegrees);
            angleInRad = inverse ? -angleInRad : angleInRad;

            // Calculate target quaternion
            const targetQuaternion = new THREE.Quaternion();
            const rotationQuaternion = this.calculateProposedQuaternion(axis, angleInRad);
            targetQuaternion.multiplyQuaternions(rotationQuaternion, mesh.quaternion);

            // Apply rotation
            this.applyRotation(mesh, targetQuaternion);
            resolve();

            // Mark the mesh as edited
            mesh.userData[Object3DUserData.initialPosition] = mesh.position;
            const rotation = mesh.rotation.clone();
            mesh.userData[Object3DUserData.rotation] = {
                x: rotation.x,
                y: rotation.y,
                z: rotation.z,
                order: rotation.order,
            };
            this.markAsEdited();
        });
    }

    private applyRotation(mesh: THREE.Mesh, targetQuaternion: THREE.Quaternion): void {
        // Apply the rotation
        mesh.quaternion.copy(targetQuaternion);
        // Update the world matrix to reflect the new rotation
        mesh.updateMatrixWorld(true);
    }

    // normal orientation => front right back left top bottom
    public flipTop(mesh?: THREE.Mesh): void {
        if (mesh) {
            this.performRotationAsync("x", mesh, false, 180).then(() => {
                this.interactionService.render();
            });
            const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
            mesh.userData[Object3DUserData.orientationData] = [
                currentOrientation[0], // front remains the same
                currentOrientation[3], // right becomes left
                currentOrientation[2], // back remains the same
                currentOrientation[1], // left becomes right
                currentOrientation[5], // top becomes bottom
                currentOrientation[4], // bottom becomes top
            ];
        } else {
            const rotationPromises = this.selectedShapes.map((mesh, _index) => {
                const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                mesh.userData[Object3DUserData.orientationData] = [
                    currentOrientation[0], // front remains the same
                    currentOrientation[3], // right becomes left
                    currentOrientation[2], // back remains the same
                    currentOrientation[1], // left becomes right
                    currentOrientation[5], // top becomes bottom
                    currentOrientation[4], // bottom becomes top
                ];
                return this.performRotationAsync("x", mesh, false, 180);
            });
            Promise.all(rotationPromises).then(() => {
                this.interactionService.render();
            });
        }
    }

    public flipLeft(mesh?: THREE.Mesh): void {
        if (mesh) {
            this.performRotationAsync("x", mesh).then(() => {
                this.interactionService.render();
            });
            const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
            mesh.userData[Object3DUserData.orientationData] = [
                currentOrientation[0], //front is same
                currentOrientation[5], //right become bottom
                currentOrientation[2], // back is same
                currentOrientation[4], //left become top
                currentOrientation[1], // top become right
                currentOrientation[3], // bottom become left
            ];
        } else {
            const rotationPromises = this.selectedShapes.map((mesh, _index) => {
                const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                mesh.userData[Object3DUserData.orientationData] = [
                    currentOrientation[0], //front is same
                    currentOrientation[5], //right become bottom
                    currentOrientation[2], // back is same
                    currentOrientation[4], //left become top
                    currentOrientation[1], // top become right
                    currentOrientation[3], // bottom become left
                ];
                return this.performRotationAsync("x", mesh);
            });
            Promise.all(rotationPromises).then(() => {
                this.interactionService.render();
            });
        }
    }

    public flipRight(mesh?: THREE.Mesh): void {
        if (mesh) {
            this.performRotationAsync("x", mesh, true).then(() => {
                this.interactionService.render();
            });
            const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
            mesh.userData[Object3DUserData.orientationData] = [
                currentOrientation[0], // front is same
                currentOrientation[4], // right become top
                currentOrientation[2], // back is same
                currentOrientation[5], // left become bottom
                currentOrientation[3], // top become left
                currentOrientation[1], // bottom become right
            ];
        } else {
            const rotationPromises = this.selectedShapes.map((mesh, _index) => {
                const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                mesh.userData[Object3DUserData.orientationData] = [
                    currentOrientation[0], // front is same
                    currentOrientation[4], // right become top
                    currentOrientation[2], // back is same
                    currentOrientation[5], // left become bottom
                    currentOrientation[3], // top become left
                    currentOrientation[1], // bottom become right
                ];
                return this.performRotationAsync("x", mesh, true);
            });
            Promise.all(rotationPromises).then(() => {
                this.interactionService.render();
            });
        }
    }

    public flipFront(mesh?: THREE.Mesh): void {
        if (mesh) {
            this.performRotationAsync("z", mesh, true).then(() => {
                this.interactionService.render();
            });
            const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
            mesh.userData[Object3DUserData.orientationData] = [
                currentOrientation[4], // front is top
                currentOrientation[1], // right is same
                currentOrientation[5], // back become bottom
                currentOrientation[3], // left is same
                currentOrientation[2], // top become back
                currentOrientation[0], // bottom become front
            ];
        } else {
            const rotationPromises = this.selectedShapes.map((mesh, _index) => {
                const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                mesh.userData[Object3DUserData.orientationData] = [
                    currentOrientation[4], // front is top
                    currentOrientation[1], // right is same
                    currentOrientation[5], // back become bottom
                    currentOrientation[3], // left is same
                    currentOrientation[2], // top become back
                    currentOrientation[0], // bottom become front
                ];
                return this.performRotationAsync("z", mesh, true);
            });
            Promise.all(rotationPromises).then(() => {
                this.interactionService.render();
            });
        }
    }

    public flipBack(mesh?: THREE.Mesh): void {
        if (mesh) {
            this.performRotationAsync("z", mesh).then(() => {
                this.interactionService.render();
            });
            const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
            mesh.userData[Object3DUserData.orientationData] = [
                currentOrientation[5], // front is bottom
                currentOrientation[1], // right is same
                currentOrientation[4], // back become top
                currentOrientation[3], // left is same
                currentOrientation[0], // top become front
                currentOrientation[2], // bottom become back
            ];
        } else {
            const rotationPromises = this.selectedShapes.map((mesh, _index) => {
                const currentOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                mesh.userData[Object3DUserData.orientationData] = [
                    currentOrientation[5], // front is bottom
                    currentOrientation[1], // right is same
                    currentOrientation[4], // back become top
                    currentOrientation[3], // left is same
                    currentOrientation[0], // top become front
                    currentOrientation[2], // bottom become back
                ];
                return this.performRotationAsync("z", mesh);
            });
            Promise.all(rotationPromises).then(() => {
                this.interactionService.render();
            });
        }
    }

    private getAction(meshes: THREE.Mesh[]): UndoRedoAction {
        return {
            actionType: Action.Edit,
            meshStates: meshes.map((mesh) => ({
                mesh: mesh,
                previousPosition: mesh.position.clone(),
                previousOrientation: [...mesh.userData[Object3DUserData.orientationData]],
                previousRotation: mesh.rotation.clone(),
            })),
        };
    }

    public saveStateIfValid(meshes: THREE.Mesh[]): void {
        if (!this.shapeValidationService.isValidationActive()) {
            const action = this.getAction(meshes);

            if (action.meshStates?.length) {
                this.undoRedoService.addToUndo(action);
            }
        }
    }
}
